import * as React from 'react';
import { Store, createStore, compose } from 'redux';
import { createAuthorizationContext, IAppContext, IConfigurationBase, isProduction } from '@agilepoint/wat';
import {
    createLocalizer, injectAuthorizationProvider, injectLocalizationProvider, ILocalizer
} from '@agilepoint/wat-react';
import { composeWithDevTools } from 'redux-devtools-extension';
import { App } from './app';
import { IApplicationConfig, IRouteBuilderContext, MenuGroup } from './types';
import modules from './modules';
import { accessRights, PermissionCode } from './lib/authorization';
import { LoadingIllustration } from './common/components';
import { deepMerge } from '@agilepoint/utils-common';

interface IAppLoadingContext {
    loading?: boolean;
    localizer?: ILocalizer;
    accessCodes?: string[];
    store?: Store;
    error?: Error;
}

const createRoutes = (appContext: IAppContext<IApplicationConfig>) => {
    const routeContext: IRouteBuilderContext = {
        baseUrl: appContext.configuration.baseUrl,
        appContext,
        getMenuItemsByGroup(group: MenuGroup) {
            return [];
        }
    };
    return modules.map(i => i.routes(routeContext).props.children).reduce((prev, curr) => prev.concat(curr), []);
};

const AppWithAuthorizerLocalizer = injectLocalizationProvider(injectAuthorizationProvider(App));

export function AppLoader(props: {
    appContext: IAppContext<IApplicationConfig>;
    culture: string;
}) {
    const { appContext, culture } = props;
    const routes = React.useMemo(() => createRoutes(appContext), [appContext]);
    const [appLoadingContext, setAppLoadingContext] = React.useState<IAppLoadingContext>({ loading: true });
    const isInProduction = isProduction(appContext.configuration.environment);

    const config: IConfigurationBase = React.useMemo(() =>
        deepMerge({
            ...appContext.configuration,
            ...{
                agilepoint: {
                    ...appContext.configuration.agilepoint,
                    culture
                }
            }
        }), [appContext.configuration, culture]);

    React.useMemo(() => {
        Promise.all(
            [
                createLocalizer({
                    configuration: config,
                    namespacesToLoad: ['app'],
                    waitTillInitialized: true,
                    debug: !isInProduction
                })
            ]).then(([localizer]) => {
                setAppLoadingContext({ loading: false, localizer });
            })
            .catch(r => setAppLoadingContext({ loading: false, error: r }));

    }, [config, isInProduction]);

    const store = React.useMemo(() => {
        const enhancer = compose();
        const composed = isInProduction ? enhancer : composeWithDevTools(enhancer);
        return createStore((state) => (state === undefined ? {} : state), composed);

    }, [isInProduction]);

    const authorizationContext = React.useMemo(() => {
        const accessCodes = (appLoadingContext.accessCodes ? appLoadingContext.accessCodes : []) as PermissionCode[];
        const authContext = createAuthorizationContext<PermissionCode>();
        authContext.authorizer.registerAccessRights(...accessRights);
        authContext.permissionStore.add(...accessCodes);
        return authContext;
    }, [appLoadingContext.accessCodes]);

    const appProps = React.useMemo(() => ({
        appContext,
        authorizationContext,
        localizer: appLoadingContext.localizer!,
        store,
        routes
    }), [appContext, authorizationContext, appLoadingContext.localizer, store, routes]);

    if (appLoadingContext.loading) {
        return (
            <LoadingIllustration />
        );
    }

    if (appLoadingContext.error) {
        return (
            <p> {appLoadingContext.error.toString()}</p>
        );
    }

    return React.createElement(AppWithAuthorizerLocalizer, appProps);
}
